import { useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { pmGet } from '../api';
import './ActionNeeded.css';

/**
 * Action Needed — everything outstanding across every initiative, milestone and project the
 * viewer is assigned to monitor, in one list.
 *
 * Built to `action-needed-screen-build-instructions.md`.
 *
 * ── THE VOCABULARY IS THE CLIENT'S, THE DATA IS OURS ───────────────────────────────────
 *
 *   Initiative  = delivery project
 *   Milestone   = opportunity
 *   a ROW       = a work item, whose DUE DATE is what the system calls a milestone
 *
 * The columns below say Initiative and Milestone because that is what the people using this
 * screen say. Getting it backwards makes every column wrong.
 *
 * ── NOTHING HERE ASSUMES HOW MUCH DATA EXISTS ──────────────────────────────────────────
 *
 * No top-N, no fixed counts, no assumption that any table is populated. Every list of
 * people, projects and opportunities is DERIVED FROM THE ROWS and built after they arrive —
 * a control populated before its data exists is empty forever, and fails silently because
 * the control still renders.
 */

interface AnChange {
  from: string;
  to: string;
  on: string;
}
interface AnRow {
  uid: string;
  pj: string;
  pjname: string;
  op: string;
  opname: string;
  item: string;
  wtype: string;
  person: string;
  email: string;
  role: string;
  status: string;
  progress: number;
  due: string | null;
  dd: number | null;
  chg: AnChange | null;
  act: { action: string; flag: string; reason: string };
}
interface AnEmpty {
  pj: string;
  pjname: string;
  n: number;
}

type Lens = 'person' | 'initiative' | 'action' | 'flat';

/** The three actions, in precedence order — the same order the server sorts by. */
const ACTIONS = [
  { k: 'Follow-up', label: 'Follow-up', tone: 'overdue', hint: 'Past its due date' },
  { k: 'Review', label: 'Review', tone: 'changed', hint: 'Status moved recently' },
  { k: 'Request Update', label: 'Request Update', tone: 'approaching', hint: 'Due soon' },
] as const;

const ACTION_NEEDED_HELP: HelpContent = {
  title: 'How this works',
  icon: 'notification_important',
  purpose:
    'One consolidated list of everything outstanding across <strong>every initiative, milestone and project you are assigned to monitor</strong> &mdash; so nobody has to open each project in turn to find it. The per-project screens are unchanged; this sits alongside them.',
  doHere: [
    'Read the list top to bottom &mdash; it is already sorted worst-first: <strong>Follow-up</strong>, then <strong>Review</strong>, then <strong>Request Update</strong>, and within each, the most overdue first.',
    '<strong>Click a card</strong> to narrow to that action; click it again to clear. The cards keep showing the full picture, so the summary stays a summary.',
    'Switch the <strong>lens</strong> to group by who to chase, where the trouble is, or what kind of action it is.',
    '<strong>Follow up</strong> opens your mail client with the message already written. Nothing is ever sent on your behalf.',
    '<strong>Open</strong> shows the whole record, including its status history, without leaving the screen.',
  ],
  fits: 'It is the hinge between planning work and running it &mdash; after scope and budget are set, before the execution screens.',
  firstTime:
    'Leave the filters alone and read the <strong>Follow-up</strong> card first: that is work whose date has already passed.',
  sections: [
    {
      label: 'How an action is decided',
      type: 'defs',
      items: [
        {
          term: 'Follow-up',
          color: '#c62828',
          def: 'The <strong>due date has passed</strong> and the item is not finished.',
        },
        {
          term: 'Review',
          color: '#8a6d1b',
          def: 'A <strong>status move</strong> is recorded within the review window (14 days).',
        },
        {
          term: 'Request Update',
          color: '#1565c0',
          def: 'The item is <strong>due within 7 days</strong>.',
        },
      ],
      note: 'A row carries <strong>one</strong> action, so precedence applies: overdue &rarr; status change &rarr; approaching. A date that has already passed is a harder fact than a status someone typed. When a row is both overdue <em>and</em> recently moved, it shows Follow-up and <strong>names the move in its reason</strong> &mdash; nothing is lost.',
    },
    {
      label: 'Why an item you expected is not here',
      type: 'html',
      html: '<strong>An item with no due date carries no action</strong> and does not appear &mdash; it cannot be late for anything. That is deliberate, not a gap. Items already at 100%, and items marked out of scope, are also absent.<br><br>An initiative with <strong>no milestones at all</strong> is invisible to any roll-up that starts at the milestone: it looks clean when it is merely empty. Those are counted separately at the top of this screen, and the count can be opened.',
    },
    {
      label: 'What you see is what you may see',
      type: 'html',
      html: 'Rows <strong>and</strong> the empty-initiative count are filtered to the delivery projects you have access to. Two people opening this screen can legitimately see different totals &mdash; the numbers are per-viewer, not global.',
    },
  ],
};

export function ActionNeeded() {
  const [rows, setRows] = useState<AnRow[]>([]);
  const [empties, setEmpties] = useState<AnEmpty[]>([]);
  const [initiativeCount, setInitiativeCount] = useState(0);
  const [candidateCount, setCandidateCount] = useState(0);
  const [noDueDateCount, setNoDueDateCount] = useState(0);
  const [approachingDays, setApproachingDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [lens, setLens] = useState<Lens>('person');
  const [card, setCard] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, true>>({});
  const [panel, setPanel] = useState<AnRow | null>(null);
  const [showEmpties, setShowEmpties] = useState(false);

  /* Filters. Empty object = no constraint, so the screen works before anything is chosen. */
  const [ownerSel, setOwnerSel] = useState<Record<string, true>>({});
  const [projSel, setProjSel] = useState<Record<string, true>>({});
  const [oppSel, setOppSel] = useState<Record<string, true>>({});
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  /*
   * A CALENDAR RANGE AND "OVERDUE" ARE IN TENSION — §3.6.
   *
   * Overdue work sits BEFORE any future range, so reading the range literally hides exactly
   * what this screen is for. The range is read literally, and this option carries the
   * overdue rows alongside it. Defaulted on, because hiding them by default would be the
   * same trap wearing a checkbox.
   */
  const [includeOverdue, setIncludeOverdue] = useState(true);

  useEffect(() => {
    pmGet<{
      success: boolean;
      rows: AnRow[];
      emptyInitiatives: AnEmpty[];
      initiativeCount: number;
      candidateCount: number;
      noDueDateCount: number;
      approachingDays: number;
      error?: string;
    }>('/action-needed')
      .then((d) => {
        if (!d.success) throw new Error(d.error ?? 'failed');
        setRows(d.rows ?? []);
        setEmpties(d.emptyInitiatives ?? []);
        setInitiativeCount(d.initiativeCount ?? 0);
        setCandidateCount(d.candidateCount ?? 0);
        setNoDueDateCount(d.noDueDateCount ?? 0);
        setApproachingDays(d.approachingDays ?? 7);
        setError(null);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  /*
   * ── ONE SCOPE FUNCTION, TWO VARIANTS ───────────────────────────────────────────────
   *
   * `scoped` applies owner, project, opportunity and the due window. `visible` additionally
   * applies the card filter.
   *
   * The cards read `scoped`; the list reads `visible`. If the cards read `visible` too,
   * selecting one card would zero the others and the summary would stop being a summary.
   */
  const scoped = useMemo(() => {
    const no = Object.keys(ownerSel).length;
    const np = Object.keys(projSel).length;
    const nq = Object.keys(oppSel).length;
    return rows.filter((r) => {
      if (no && !ownerSel[r.person]) return false;
      if (np && !projSel[r.pj]) return false;
      if (nq && !oppSel[r.op || '']) return false;
      if (fromDate || toDate) {
        const overdue = r.dd != null && r.dd < 0;
        if (overdue && includeOverdue) return true;
        if (!r.due) return false;
        if (fromDate && r.due < fromDate) return false;
        if (toDate && r.due > toDate) return false;
      }
      return true;
    });
  }, [rows, ownerSel, projSel, oppSel, fromDate, toDate, includeOverdue]);

  const visible = useMemo(
    () => (card ? scoped.filter((r) => r.act.action === card) : scoped),
    [scoped, card],
  );

  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    ACTIONS.forEach((a) => (m[a.k] = scoped.filter((r) => r.act.action === a.k).length));
    return m;
  }, [scoped]);

  /* Derived from the rows, after they arrive — never at page load. */
  const owners = useMemo(() => [...new Set(rows.map((r) => r.person))].sort(), [rows]);
  const projects = useMemo(
    () => [...new Set(rows.map((r) => `${r.pj} ${r.pjname}`))].sort(),
    [rows],
  );

  /*
   * THE OPPORTUNITY LIST IS REBUILT FROM THE SELECTED PROJECTS, NEVER HIDDEN.
   *
   * A hidden option remains selectable and keeps filtering invisibly. So "All milestones"
   * always means all milestones OF THE PROJECTS CURRENTLY CHOSEN.
   */
  const opportunities = useMemo(() => {
    const np = Object.keys(projSel).length;
    const src = np ? rows.filter((r) => projSel[r.pj]) : rows;
    return [...new Set(src.filter((r) => r.op).map((r) => `${r.op} ${r.opname}`))].sort();
  }, [rows, projSel]);

  /* …and a selection outside the new project set is DROPPED, not merely hidden. */
  useEffect(() => {
    const allowed = new Set(opportunities.map((o) => o.split(' ')[0]!));
    setOppSel((cur) => {
      const next: Record<string, true> = {};
      let dropped = false;
      Object.keys(cur).forEach((k) => (allowed.has(k) ? (next[k] = true) : (dropped = true)));
      return dropped ? next : cur;
    });
  }, [opportunities]);

  const groups = useMemo(() => {
    if (lens === 'flat') return null;
    const m = new Map<string, AnRow[]>();
    visible.forEach((r) => {
      const k =
        lens === 'person'
          ? r.person
          : lens === 'initiative'
            ? `${r.pj} — ${r.pjname}`
            : r.act.action;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    });
    return [...m.entries()];
  }, [visible, lens]);

  /*
   * FOLLOW UP IS THE EMAIL — §3.8. A mailto: built from the record and handed to the mail
   * client. No composer, no intermediate step, and nothing is ever sent on the user's
   * behalf. Where there is no address the button is disabled and says why, rather than
   * opening an empty message.
   */
  const mailto = (r: AnRow) => {
    const subject = `${r.act.action}: ${r.item}`;
    const body = [
      `Work item: ${r.item}`,
      `Initiative: ${r.pj}${r.pjname ? ` — ${r.pjname}` : ''}`,
      `Milestone: ${r.op ? `${r.op} — ${r.opname}` : 'At initiative level'}`,
      `Due: ${r.due ?? 'not set'}`,
      `Status: ${r.status} (${r.progress}%)`,
      '',
      r.act.reason,
    ].join('\n');
    return `mailto:${encodeURIComponent(r.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const row = (r: AnRow) => (
    <tr key={r.uid} className="an-row">
      <td>
        <span className={`an-flag ${r.act.flag}`}>{r.act.action}</span>
      </td>
      <td>
        <div className="an-item">{r.item}</div>
        <div className="an-sub">
          {r.wtype} · {r.person}
          {r.role ? ` · ${r.role}` : ''}
        </div>
      </td>
      <td>
        <div className="an-item">{r.pj}</div>
        <div className="an-sub">{r.pjname}</div>
      </td>
      <td>
        {/* An item may sit at INITIATIVE LEVEL — that is not a blank cell, it is a fact. */}
        {r.op ? (
          <>
            <div className="an-item">{r.op}</div>
            <div className="an-sub">{r.opname}</div>
          </>
        ) : (
          <span className="an-none">At initiative level</span>
        )}
      </td>
      <td className="an-due">
        {r.due ?? <span className="an-none">No due date</span>}
        {r.dd != null ? (
          <div className={`an-sub ${r.dd < 0 ? 'late' : ''}`}>
            {r.dd < 0 ? `${Math.abs(r.dd)}d overdue` : r.dd === 0 ? 'due today' : `in ${r.dd}d`}
          </div>
        ) : null}
      </td>
      <td>
        <div className="an-item">{r.status}</div>
        <div className="an-sub">{r.progress}%</div>
      </td>
      <td className="an-reason">{r.act.reason}</td>
      <td className="an-acts">
        {r.email ? (
          <a className="an-btn" href={mailto(r)}>
            Follow up
          </a>
        ) : (
          <span className="an-btn disabled" title="No email address on file for this owner">
            Follow up
          </span>
        )}
        <button type="button" className="an-btn ghost" onClick={() => setPanel(r)}>
          Open
        </button>
      </td>
    </tr>
  );

  const table = (list: AnRow[]) => (
    <table className="an-table">
      <thead>
        <tr>
          <th>Action</th>
          <th>Work item</th>
          <th>Initiative</th>
          <th>Milestone</th>
          <th>Due</th>
          <th>Status</th>
          <th>Why</th>
          <th />
        </tr>
      </thead>
      <tbody>{list.map(row)}</tbody>
    </table>
  );

  if (loading) return <div className="an-page">Loading…</div>;

  return (
    <div className="an-page">
      <div className="an-head">
        <div>
          <h1 className="an-title">Action Needed</h1>
          <p className="an-sub-title">
            Everything outstanding across every initiative you monitor, in one list.
          </p>
        </div>
        <HelpPanel help={ACTION_NEEDED_HELP} />
      </div>

      {error ? <div className="an-error">{error}</div> : null}

      {/* Cards read `scoped`, never `visible` — see the note on the scope function. */}
      <div className="an-cards">
        {ACTIONS.map((a) => (
          <button
            type="button"
            key={a.k}
            className={`an-card ${a.tone}${card === a.k ? ' on' : ''}`}
            aria-pressed={card === a.k}
            onClick={() => setCard((c) => (c === a.k ? null : a.k))}
          >
            <span className="n">{counts[a.k] ?? 0}</span>
            <span className="l">{a.label}</span>
            <span className="h">{a.hint}</span>
          </button>
        ))}

        {/*
          §3.3 — an initiative with no milestones appears CLEAN when it is merely EMPTY.
          Counted separately, and the count opens.
        */}
        <button
          type="button"
          className={`an-card empty${showEmpties ? ' on' : ''}`}
          aria-pressed={showEmpties}
          onClick={() => setShowEmpties((v) => !v)}
        >
          <span className="n">{empties.length}</span>
          <span className="l">No milestones loaded</span>
          <span className="h">of {initiativeCount} initiatives</span>
        </button>
      </div>

      {showEmpties ? (
        <div className="an-empties">
          {empties.length === 0 ? (
            <div className="an-note">Every initiative you can see has at least one milestone.</div>
          ) : (
            <table className="an-table">
              <thead>
                <tr>
                  <th>Initiative</th>
                  <th>Milestone</th>
                </tr>
              </thead>
              <tbody>
                {empties.map((e) => (
                  <tr key={e.pj} className="an-row">
                    <td>
                      <div className="an-item">{e.pj}</div>
                      <div className="an-sub">{e.pjname}</div>
                    </td>
                    <td>
                      <span className="an-none">No milestones loaded</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}

      <div className="an-filters">
        <Picker label="Owner" options={owners} sel={ownerSel} set={setOwnerSel} />
        <Picker
          label="Initiative"
          options={projects.map((p) => p.split(' ')[0]!)}
          labels={Object.fromEntries(
            projects.map((p) => [p.split(' ')[0]!, p.replace(' ', ' — ')]),
          )}
          sel={projSel}
          set={setProjSel}
        />
        <Picker
          label="Milestone"
          options={opportunities.map((o) => o.split(' ')[0]!)}
          labels={Object.fromEntries(
            opportunities.map((o) => [o.split(' ')[0]!, o.replace(' ', ' — ')]),
          )}
          sel={oppSel}
          set={setOppSel}
        />
        <label className="an-f">
          <span>Due from</span>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label className="an-f">
          <span>to</span>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <label className="an-chk" title="Overdue work sits before any future range, so a literal range would hide exactly what this screen is for.">
          <input
            type="checkbox"
            checked={includeOverdue}
            onChange={(e) => setIncludeOverdue(e.target.checked)}
          />
          Include overdue
        </label>
      </div>

      <div className="an-tabs" role="tablist">
        {(
          [
            ['person', 'By owner'],
            ['initiative', 'By initiative'],
            ['action', 'By action'],
            ['flat', 'Flat list'],
          ] as [Lens, string][]
        ).map(([k, l]) => (
          <button
            type="button"
            key={k}
            className={`an-tab${lens === k ? ' on' : ''}`}
            data-label={l}
            onClick={() => setLens(k)}
          >
            <span>{l}</span>
          </button>
        ))}
        <span className="an-count">
          <b>{visible.length}</b> of {scoped.length} need action
          {approachingDays !== 7 ? ` · due within ${approachingDays}d` : ''}
        </span>
      </div>

      <div className="an-body">
        {rows.length === 0 ? (
          <div className="an-note">
            {/*
              THE EMPTY STATE HAS TO SAY WHICH EMPTY IT IS.

              "Nothing to do" and "nothing has a due date" look identical on screen and mean
              completely different things — the first is good news, the second is a data gap
              that makes this screen unable to work at all. Naming the count is what stops an
              empty list reading as a broken one.
            */}
            {initiativeCount === 0 ? (
              'Nothing is assigned to you. An administrator can grant access to delivery projects under Administration.'
            ) : noDueDateCount > 0 && noDueDateCount === candidateCount ? (
              <>
                <strong>
                  None of the {candidateCount} open work items you can see has a due date.
                </strong>{' '}
                An action is decided from that date, so nothing can appear here until one is
                set — an item with no due date cannot be late for anything. This is a gap in
                the plan, not an empty to-do list.
              </>
            ) : noDueDateCount > 0 ? (
              <>
                Nothing needs action in this view. {noDueDateCount} of {candidateCount} open
                work items carry no due date and are not assessed.
              </>
            ) : (
              'Nothing needs action in this view. Every work item you can see is either finished, out of scope, or not yet due.'
            )}
          </div>
        ) : visible.length === 0 ? (
          <div className="an-note">Nothing matches these filters.</div>
        ) : groups ? (
          groups.map(([k, list]) => {
            const shut = !open[k]; /* Open collapsed, in every lens — §3.7. */
            return (
              <div className={`an-grp${shut ? '' : ' open'}`} key={k}>
                <button
                  type="button"
                  className="an-gh"
                  onClick={() =>
                    setOpen((c) => {
                      const n = { ...c };
                      if (n[k]) delete n[k];
                      else n[k] = true;
                      return n;
                    })
                  }
                >
                  <span className="material-icons an-car">chevron_right</span>
                  <span className="an-gn">{k}</span>
                  {/* Group headers carry per-action counts, so a collapsed screen still
                      says everything. */}
                  {ACTIONS.map((a) => {
                    const n = list.filter((r) => r.act.action === a.k).length;
                    return n ? (
                      <span className={`an-chip ${a.tone}`} key={a.k}>
                        {n} {a.label}
                      </span>
                    ) : null;
                  })}
                  <span className="an-gcount">{list.length}</span>
                </button>
                {shut ? null : <div className="an-gb">{table(list)}</div>}
              </div>
            );
          })
        ) : (
          table(visible)
        )}
      </div>

      {/* Read-only right-hand panel — §3.8. Deliberately does NOT link to a per-project
          screen: those are scoped to one project and take no item target, so the link would
          land the user somewhere unrelated. No row action is repeated in here. */}
      <div className={`an-scrim${panel ? ' on' : ''}`} onClick={() => setPanel(null)} />
      <aside className={`an-panel${panel ? ' open' : ''}`} aria-label="Work item">
        <div className="an-panel-hd">
          <span className="t">{panel?.item ?? ''}</span>
          <button type="button" onClick={() => setPanel(null)} aria-label="Close">
            ×
          </button>
        </div>
        {panel ? (
          <div className="an-panel-bd">
            <Field k="Action" v={panel.act.action} />
            <Field k="Why" v={panel.act.reason} />
            <Field k="Type" v={panel.wtype} />
            <Field k="Initiative" v={`${panel.pj}${panel.pjname ? ` — ${panel.pjname}` : ''}`} />
            <Field
              k="Milestone"
              v={panel.op ? `${panel.op} — ${panel.opname}` : 'At initiative level'}
            />
            <Field k="Owner" v={panel.person} />
            <Field k="Role" v={panel.role || '—'} />
            <Field k="Email" v={panel.email || 'No address on file'} />
            <Field k="Status" v={`${panel.status} · ${panel.progress}%`} />
            <Field k="Due" v={panel.due ?? 'No due date'} />
            <div className="an-panel-h4">Status history</div>
            {panel.chg ? (
              <p className="an-panel-p">
                {panel.chg.from || '(none)'} → {panel.chg.to || '(none)'} on {panel.chg.on}
              </p>
            ) : (
              <p className="an-panel-p an-none">
                No status change recorded in the review window.
              </p>
            )}
          </div>
        ) : null}
      </aside>
    </div>
  );
}

function Field({ k, v }: { k: string; v: string }) {
  return (
    <div className="an-fld">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

/**
 * The filter control — button + panel, Ctrl/Cmd-click to pick several.
 *
 * Same interaction the rest of the app uses: plain click selects one and applies; Ctrl-click
 * toggles and keeps the panel open; click-away applies.
 */
function Picker({
  label,
  options,
  labels,
  sel,
  set,
}: {
  label: string;
  options: string[];
  labels?: Record<string, string>;
  sel: Record<string, true>;
  set: (next: Record<string, true>) => void;
}) {
  const [open, setOpen] = useState(false);
  const n = Object.keys(sel).length;
  return (
    <span className="an-pick">
      <button type="button" className="an-pick-btn" onClick={() => setOpen((o) => !o)}>
        {label}: <b>{n === 0 ? `All ${label.toLowerCase()}s` : `${n} of ${options.length}`}</b> ▾
      </button>
      {n ? (
        <button type="button" className="an-lnk" onClick={() => set({})}>
          Clear
        </button>
      ) : null}
      {open ? (
        <>
          <div className="an-pick-scrim" onClick={() => setOpen(false)} />
          <div className="an-pick-panel">
            <div className="an-pick-hd">
              <button
                type="button"
                onClick={() => set(Object.fromEntries(options.map((o) => [o, true])) as Record<string, true>)}
              >
                All
              </button>
              <button type="button" onClick={() => set({})}>
                Clear
              </button>
              <span className="an-pick-hint">Ctrl-click for several</span>
            </div>
            <div className="an-pick-list">
              {options.length === 0 ? (
                <div className="an-pick-empty">Nothing to filter yet.</div>
              ) : (
                options.map((o) => (
                  <div
                    key={o}
                    className={`an-pick-row${sel[o] ? ' on' : ''}`}
                    onClick={(ev: React.MouseEvent) => {
                      if (ev.ctrlKey || ev.metaKey) {
                        const next = { ...sel };
                        if (next[o]) delete next[o];
                        else next[o] = true;
                        set(next);
                      } else {
                        set({ [o]: true });
                        setOpen(false);
                      }
                    }}
                  >
                    <span className="tick">{sel[o] ? '✓' : ''}</span>
                    {labels?.[o] ?? o}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      ) : null}
    </span>
  );
}
